import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Grid,
  GridItem } from
'@chakra-ui/react';
import { AlertOctagon, TrendingDown, Clock, ShieldOff } from 'lucide-react';
const MotionBox = motion(Box);
export function ComparisonSection() {
  return (
    <Box as="section" py={24} position="relative">
      <Container maxW="container.xl" px={4}>
        {/* Comparison Table */}
        <Box maxW="4xl" mx="auto" mb={32}>
          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            viewport={{
              once: true
            }}
            textAlign="center"
            mb={12}>
            
            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold"
              mb={4}>
              
              Why most "GMC experts" fail
            </Heading>
            <Text fontSize="xl" color="gray.400">
              They look at your store like a human.{' '}
              <Text as="span" color="white" fontWeight="semibold">
                Google doesn't.
              </Text>
            </Text>
          </MotionBox>

          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            viewport={{
              once: true
            }}
            overflow="hidden"
            borderRadius="2xl"
            border="1px solid"
            borderColor="brand.border"
            bg="brand.card">
            
            {/* Header */}
            <Grid
              templateColumns="repeat(3, 1fr)"
              borderBottom="1px solid"
              borderColor="brand.border"
              bg="rgba(13, 26, 18, 0.5)">
              
              <GridItem p={6} fontWeight="semibold" color="gray.400">
                Feature
              </GridItem>
              <GridItem
                p={6}
                fontWeight="bold"
                color="brand.green"
                borderLeft="1px solid"
                borderColor="brand.border"
                bg="rgba(16, 185, 129, 0.05)">
                
                GMCRadar
              </GridItem>
              <GridItem
                p={6}
                fontWeight="semibold"
                color="gray.400"
                borderLeft="1px solid"
                borderColor="brand.border">
                
                "GMC Expert"
              </GridItem>
            </Grid>

            {[
            {
              label: 'Cost',
              ours: '$49 one-time',
              theirs: '$1,000–$3,000+'
            },
            {
              label: 'Speed',
              ours: '~60 seconds',
              theirs: '1–2 weeks'
            },
            {
              label: 'Accuracy',
              ours: 'System-based (Google-style)',
              theirs: 'Human guesswork'
            },
            {
              label: 'Coverage',
              ours: '200+ checks',
              theirs: 'Limited'
            },
            {
              label: 'Re-scan',
              ours: 'Included',
              theirs: 'Extra cost'
            },
            {
              label: 'Consistency',
              ours: 'Exact every time',
              theirs: 'Varies'
            }].
            map((row, i, arr) =>
            <Grid
              key={i}
              templateColumns="repeat(3, 1fr)"
              borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
              borderColor="brand.border">
              
                <GridItem
                p={{
                  base: 4,
                  md: 6
                }}
                color="gray.300"
                fontWeight="medium"
                display="flex"
                alignItems="center">
                
                  {row.label}
                </GridItem>
                <GridItem
                p={{
                  base: 4,
                  md: 6
                }}
                color="white"
                fontWeight="semibold"
                borderLeft="1px solid"
                borderColor="brand.border"
                bg="rgba(16, 185, 129, 0.05)"
                display="flex"
                alignItems="center">
                
                  {row.ours}
                </GridItem>
                <GridItem
                p={{
                  base: 4,
                  md: 6
                }}
                color="gray.500"
                borderLeft="1px solid"
                borderColor="brand.border"
                display="flex"
                alignItems="center">
                
                  {row.theirs}
                </GridItem>
              </Grid>
            )}
          </MotionBox>

          <Box textAlign="center" mt={8}>
            <Text fontSize="xl" fontWeight="medium" color="brand.green">
              You don't need opinions. You need to see what Google sees.
            </Text>
          </Box>
        </Box>

        {/* Fear + Consequence */}
        <Box maxW="4xl" mx="auto">
          <MotionBox
            initial={{
              opacity: 0,
              scale: 0.95
            }}
            whileInView={{
              opacity: 1,
              scale: 1
            }}
            viewport={{
              once: true
            }}
            borderRadius="2xl"
            border="1px solid"
            borderColor="rgba(239, 68, 68, 0.2)"
            bg="rgba(239, 68, 68, 0.05)"
            p={{
              base: 8,
              md: 12
            }}
            position="relative"
            overflow="hidden">
            
            <Box
              position="absolute"
              top={0}
              right={0}
              w="256px"
              h="256px"
              bg="rgba(239, 68, 68, 0.1)"
              filter="blur(80px)"
              borderRadius="full"
              pointerEvents="none" />
            

            <Box position="relative" zIndex={10}>
              <Flex align="center" gap={3} mb={6}>
                <AlertOctagon color="#ef4444" size={32} />
                <Heading
                  as="h2"
                  fontSize={{
                    base: '2xl',
                    md: '3xl'
                  }}
                  fontWeight="bold"
                  color="white">
                  
                  Appealing without fixing root causes is dangerous.
                </Heading>
              </Flex>

              <SimpleGrid
                columns={{
                  base: 1,
                  md: 2
                }}
                spacing={6}
                mt={8}>
                
                {[
                {
                  icon: Clock,
                  text: 'Google logs every failed appeal'
                },
                {
                  icon: TrendingDown,
                  text: 'Trust signals get weaker over time'
                },
                {
                  icon: ShieldOff,
                  text: 'Reviews become stricter'
                },
                {
                  icon: AlertOctagon,
                  text: 'Reinstatement becomes harder'
                }].
                map((item, i) =>
                <Flex
                  key={i}
                  align="center"
                  gap={3}
                  bg="rgba(13, 26, 18, 0.5)"
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="rgba(239, 68, 68, 0.1)">
                  
                    <Box flexShrink={0}>
                      <item.icon color="#f87171" size={20} />
                    </Box>
                    <Text color="gray.300" fontWeight="medium">
                      {item.text}
                    </Text>
                  </Flex>
                )}
              </SimpleGrid>

              <Box
                mt={8}
                pt={8}
                borderTop="1px solid"
                borderColor="rgba(239, 68, 68, 0.1)"
                textAlign="center">
                
                <Text fontSize="xl" fontWeight="bold" color="red.400">
                  This is why most stores stay banned for months.
                </Text>
              </Box>
            </Box>
          </MotionBox>
        </Box>
      </Container>
    </Box>);

}